import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Tech.css";

/* ─── データ ────────────────────────────────────────────────────── */
const FEATURES = [
  {
    num: "01",
    en: "Simulation",
    ja: "高精度CFDシミュレーション",
    body: "Cradle CFD（scFLOW / scPOST）を用い、気流・温度・湿度・圧力分布を三次元で可視化。設計段階の潜在リスクを早期に発見し、試作コストとリードタイムを大幅に削減します。",
    img: "/images/feature1.png",
  },
  {
    num: "02",
    en: "Expertise",
    ja: "60年超の解析ノウハウ",
    body: "半導体製造装置の洗浄プロセスに特化した長年の知見をベースに、構造・熱・流体の複合解析を実施。現場の「困った」を的確に「解けた」へ変換します。",
    img: "/images/feature2.png",
  },
];

const GALLERY_ITEMS = [
  { src: "/images/feature1.png", en: "3D Model", ja: "解析条件 / ３Dモデル" },
  { src: "/images/feature2.png", en: "Airflow",  ja: "エアコン気流" },
  { src: "/images/feature3.png", en: "Humidity", ja: "湿度分布" },
  { src: "/images/feature4.png", en: "Flow Vector", ja: "気流ベクトル" },
];

/* ─── カウントアップ hook ───────────────────────────────────────── */
function useCountUp(target, duration, go) {
  const [v, setV] = useState("0");
  useEffect(() => {
    if (!go) return;
    const n = parseFloat(target);
    const sfx = target.replace(/[\d.]/g, "");
    if (isNaN(n)) { setV(target); return; }
    let t0 = null;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setV((e * n).toFixed(target.includes(".") ? 1 : 0) + sfx);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [go, target, duration]);
  return v;
}

function StatItem({ value, sup, label }) {
  const ref = useRef(null);
  const [go, setGo] = useState(false);
  const v = useCountUp(value, 1600, go);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setGo(true); obs.disconnect(); } },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="tp-stat">
      <span className="tp-stat-num">
        {go ? v : "0"}<span className="tp-stat-sup">{sup}</span>
      </span>
      <span className="tp-stat-label">{label}</span>
    </div>
  );
}

/* ─── メインコンポーネント ──────────────────────────────────────── */
export default function Tech() {
  const demoVideoRef = useRef(null);
  const [loaded,    setLoaded]    = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollY,   setScrollY]   = useState(0);
  const tickRef = useRef(false);

  /* マウント直後にヒーロー文字を表示 */
  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  /* スクロール検知（rAF スロットリング） */
  useEffect(() => {
    const h = () => {
      if (tickRef.current) return;
      tickRef.current = true;
      requestAnimationFrame(() => { setScrollY(window.scrollY); tickRef.current = false; });
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  /* スクロールリビール */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    document.querySelectorAll("[data-r]").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const togglePlay = useCallback(() => {
    if (!demoVideoRef.current) return;
    demoVideoRef.current.paused ? demoVideoRef.current.play() : demoVideoRef.current.pause();
  }, []);

  /* Products と同じパララックス計算 */
  const videoY   = Math.min(scrollY * 0.35, 110);
  const contentO = Math.max(1 - scrollY / 460, 0);
  const contentY = Math.min(scrollY * 0.16, 60);

  const r = loaded;

  return (
    <div className="tp">
      <div id="page-top" />

      {/* ════ HERO — 動画パララックス（Products と同方式） ════ */}
      <section className={`tp-hero${r ? " loaded" : ""}`}>

        {/* 背景動画 */}
        <div className="tp-hero-media">
          <video
            className="tp-hero-video"
            autoPlay loop muted playsInline disablePictureInPicture
            style={{ transform: `translate(-50%, calc(-50% + ${videoY}px))` }}
          >
            <source src="/videos/Tech-hero.mp4" type="video/mp4" />
          </video>
          <div className="tp-hero-grad" />
        </div>

        {/* コンテンツ（スクロールでフェード） */}
        <div
          className="tp-hero-copy"
          style={{ opacity: contentO, transform: `translateY(${contentY}px)` }}
        >
          {/* 左上ラベル */}
          <p className="tp-badge">CFD Simulation Technology</p>

          {/* 行ごとクリップイン */}
          <h1 className="tp-hero-h1">
            <span className="tp-cl delay-0">SOLVE</span>
            <span className="tp-cl delay-1">THE</span>
            <span className="tp-cl tp-cl--sky delay-2">FLUID.</span>
          </h1>

          <div className="tp-hero-sub">
            <p className="tp-hero-ja">流体の謎を、データで解き明かす。</p>
            <p className="tp-hero-en">
              High-accuracy CFD simulation for semiconductor cleaning processes.
            </p>
          </div>

          <div className="tp-hero-cta">
            <Link to="/contact#page-top" className="tp-hero-btn">
              お問い合わせ <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        {/* 右下スクロール */}
        <div className="tp-scroll-ui" aria-hidden="true">
          <div className="tp-scroll-track"><div className="tp-scroll-runner" /></div>
          <span>SCROLL</span>
        </div>

        {/* 右縦テキスト */}
        <p className="tp-side-text" aria-hidden="true">
          Cradle CFD · scFLOW · scPOST
        </p>
      </section>

      {/* ════ INTRO ════ */}
      <section className="tp-intro">
        <div className="tp-intro-inner">
          <p className="tp-tag" data-r>Our Technology</p>
          <h2 className="tp-intro-h2" data-r>
            設計の<em>見えない問題</em>を、<br />シミュレーションで可視化する。
          </h2>
          <p className="tp-intro-body" data-r>
            半導体洗浄装置の開発において、流体挙動の把握は品質と効率の要です。
            弊社は60年超の解析実績と最新のCAEツールを組み合わせ、
            気流・熱・湿度の複合シミュレーションを提供。
            設計段階でのリスクゼロを目指します。
          </p>
        </div>
        <div className="tp-intro-stats">
          <StatItem value="60"  sup="+" label="Years of Experience" />
          <StatItem value="500" sup="+" label="Analysis Projects" />
          <StatItem value="3"   sup="D" label="High-Accuracy Model" />
        </div>
      </section>

      {/* ════ FEATURES ════ */}
      <section className="tp-features">
        {FEATURES.map((f, i) => (
          <div key={f.num} className={`tp-feat${i % 2 === 1 ? " tp-feat--rev" : ""}`} data-r>
            {/* 画像 */}
            <div className="tp-feat-media">
              <img src={f.img} alt={f.ja} className="tp-feat-img" />
              <div className="tp-feat-num-bg" aria-hidden="true">{f.num}</div>
            </div>
            {/* テキスト */}
            <div className="tp-feat-text">
              <p className="tp-tag">{f.en}</p>
              <h3 className="tp-feat-h3">{f.ja}</h3>
              <div className="tp-feat-rule" />
              <p className="tp-feat-body">{f.body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* ════ VIDEO ════ */}
      <section className="tp-video" data-r>
        <div className="tp-sec-head">
          <p className="tp-tag">Simulation Demo</p>
          <h2 className="tp-sec-h2">シミュレーション映像</h2>
        </div>
        <div
          className={`tp-vbox${isPlaying ? " on" : ""}`}
          onClick={togglePlay}
          role="button" tabIndex={0}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && togglePlay()}
          aria-label={isPlaying ? "一時停止" : "シミュレーション映像を再生"}
        >
          <video
            ref={demoVideoRef}
            src="/videos/techtop.mp4"
            className="tp-video-el"
            muted playsInline
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          <div className="tp-vbox-film" />
          {!isPlaying && (
            <div className="tp-play-btn" aria-hidden="true">
              <div className="tp-play-ring" />
              <div className="tp-play-tri" />
            </div>
          )}
        </div>
      </section>

      {/* ════ GALLERY ════ */}
      <section className="tp-gallery">
        <div className="tp-sec-head" data-r>
          <p className="tp-tag">Analysis Gallery</p>
          <h2 className="tp-sec-h2">解析ビジュアル</h2>
        </div>
        <div className="tp-gal-grid">
          {GALLERY_ITEMS.map((g, i) => (
            <figure
              key={g.en}
              className="tp-gal-item"
              data-r
              style={{ transitionDelay: `${i * 0.07}s` }}
            >
              <div className="tp-gal-img-box">
                <img src={g.src} alt={g.ja} className="tp-gal-img" />
              </div>
              <figcaption className="tp-gal-cap">
                <span className="tp-gal-en">{g.en}</span>
                <span className="tp-gal-ja">{g.ja}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ════ CTA ════ */}
      <section className="tp-cta" data-r>
        <div className="tp-cta-bg" aria-hidden="true" />
        <div className="tp-cta-inner">
          <p className="tp-cta-tag">Contact</p>
          <h2 className="tp-cta-h2">
            シミュレーション解析について<br />お気軽にご相談ください
          </h2>
          <p className="tp-cta-sub">
            プロセス改善・新規設計・既存装置の解析など、まずはお問い合わせください。
          </p>
          <Link to="/contact#page-top" className="tp-cta-btn">
            お問い合わせはこちら <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

    </div>
  );
}
